import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  ChartBarIcon,
  CpuChipIcon,
  CreditCardIcon,
  UserGroupIcon,
  BanknotesIcon,
  CogIcon,
  BellIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  SunIcon,
  MoonIcon,
  LanguageIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';

interface DashboardLayoutProps {
  children: React.ReactNode;
  currentPage: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, currentPage }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const { language, toggleLanguage, t, direction } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  const navigation = [
    { name: t('dashboard.overview'), href: '/dashboard', icon: HomeIcon, current: location.pathname === '/dashboard' },
    { name: t('dashboard.analytics'), href: '/dashboard/analytics', icon: ChartBarIcon, current: location.pathname === '/dashboard/analytics' },

    { name: t('dashboard.cards'), href: '/dashboard/smart-cards', icon: CpuChipIcon, current: location.pathname === '/dashboard/smart-cards' },
    { name: t('dashboard.partnerships'), href: '/dashboard/partnerships', icon: UserGroupIcon, current: location.pathname === '/dashboard/partnerships' },
    { name: t('dashboard.financing'), href: '/dashboard/financing', icon: BanknotesIcon, current: location.pathname === '/dashboard/financing' },

    { name: t('dashboard.compliance'), href: '/dashboard/compliance', icon: ShieldCheckIcon, current: location.pathname === '/dashboard/compliance' },
    { name: t('dashboard.hr_mudad'), href: '/dashboard/wage-protection', icon: ShieldCheckIcon, current: location.pathname === '/dashboard/wage-protection' },
    { name: t('dashboard.settings'), href: '/dashboard/settings', icon: CogIcon, current: location.pathname === '/dashboard/settings' },
  ];

  const userNavigation = [
    { name: t('dashboard.profile'), href: '/dashboard/profile' },
    { name: t('dashboard.account'), href: '/dashboard/account' },
    { name: t('dashboard.logout'), href: '/login' },
  ];

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900`} dir={direction}>
      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 lg:hidden"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
            </motion.div>

            <motion.div
              initial={{ x: direction === 'rtl' ? 256 : -256 }}
              animate={{ x: 0 }}
              exit={{ x: direction === 'rtl' ? 256 : -256 }}
              className={`fixed top-0 ${direction === 'rtl' ? 'right-0' : 'left-0'} z-50 w-64 h-full bg-white dark:bg-gray-800 shadow-xl lg:hidden`}
            >
              <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">F</span>
                  </div>
                  <span className="ml-2 rtl:ml-0 rtl:mr-2 text-xl font-bold text-gray-900 dark:text-white">FLYN</span>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <nav className="mt-5 px-2">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      item.current
                        ? 'bg-gradient-primary text-white shadow-lg'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    } group flex items-center px-3 py-2 text-sm font-medium rounded-lg mb-1 transition-all duration-200`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon
                      className={`${
                        item.current ? 'text-white' : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                      } ml-3 rtl:ml-0 rtl:mr-3 flex-shrink-0 h-5 w-5`}
                      aria-hidden="true"
                    />
                    {item.name}
                  </Link>
                ))}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col ${
        direction === 'rtl' ? 'lg:right-0' : 'lg:left-0'
      }`}>
        <div className={`flex flex-col flex-grow pt-5 bg-white dark:bg-gray-800 shadow-xl ${
          direction === 'rtl' 
            ? 'border-l border-gray-200 dark:border-gray-700' 
            : 'border-r border-gray-200 dark:border-gray-700'
        }`}>
          <div className="flex items-center flex-shrink-0 px-4">
            <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">F</span>
            </div>
            <span className="ml-3 rtl:ml-0 rtl:mr-3 text-2xl font-bold text-gray-900 dark:text-white">FLYN</span>
          </div>
          <div className="mt-8 flex-grow flex flex-col">
            <nav className="flex-1 px-2 pb-4 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    item.current
                      ? 'bg-gradient-primary text-white shadow-lg transform scale-105'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:transform hover:scale-105'
                  } group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200`}
                >
                  <item.icon
                    className={`${
                      item.current ? 'text-white' : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                    } ml-3 rtl:ml-0 rtl:mr-3 flex-shrink-0 h-6 w-6`}
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={direction === 'rtl' ? 'lg:pr-64' : 'lg:pl-64'}>
        {/* Top navigation */}
        <div className="sticky top-0 z-30 flex h-16 flex-shrink-0 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            className="px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex flex-1 justify-between px-4 lg:px-6">
            <div className="flex flex-1">
              <div className="flex w-full md:ml-0">
                <div className="relative w-full text-gray-400 focus-within:text-gray-600 dark:focus-within:text-gray-300">
                  <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 flex items-center pointer-events-none pl-3 rtl:pl-0 rtl:pr-3">
                    <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <input
                    className="block w-full h-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2 border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-0 focus:border-transparent bg-transparent"
                    placeholder={t('dashboard.search')}
                    type="search"
                  />
                </div>
              </div>
            </div>

            <div className="ml-4 rtl:ml-0 rtl:mr-4 flex items-center md:ml-6 rtl:md:ml-0 rtl:md:mr-6 space-x-4 rtl:space-x-reverse">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
              >
                {theme === 'dark' ? (
                  <SunIcon className="h-5 w-5" />
                ) : (
                  <MoonIcon className="h-5 w-5" />
                )}
              </button>

              {/* Language toggle */}
              <button
                onClick={toggleLanguage}
                className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
              >
                <LanguageIcon className="h-5 w-5" />
              </button>

              {/* Notifications */}
              <button className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200 relative">
                <BellIcon className="h-5 w-5" />
                <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white dark:ring-gray-800"></span>
              </button>

              {/* Profile dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <UserCircleIcon className="h-8 w-8 text-gray-400" />
                </button>

                <AnimatePresence>
                  {profileMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className={`absolute ${direction === 'rtl' ? 'left-0' : 'right-0'} z-10 mt-2 w-48 origin-top-right rounded-lg bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
                    >
                      {userNavigation.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
